import { SEAT_GRACE_MS, type MwReleasedPayload } from '@badger-sentinel/protocol';
import { errorTitle } from '../../game/errors.js';

import type { CountdownView } from './countdown.js';
import type { SeatSocketStatus } from '../../ws/useSeatSocket.js';

/**
 * The one alert channel (shell.md §Interrupts).
 *
 * Owned by the shell and raised into by windows, because anything that can
 * interrupt you *regardless of what you are looking at* is shell: a GM inject,
 * a rejection, a missile-warning release, the deadline entering its last minute,
 * the socket dropping.
 * Windows get their own row treatment; this is the thing that reaches somebody
 * reading a different part of the screen.
 */

export interface SeatAlert {
  /** Stable across renders so a dismissal sticks to the alert it was aimed at. */
  readonly key: string;
  readonly tone: 'error' | 'warning';
  readonly title: string;
  readonly body: string;
  readonly code: string | null;
  readonly correlationId: string | null;
}

export interface AlertSources {
  readonly errors: readonly {
    readonly id: number;
    readonly code: string;
    readonly message: string;
    readonly correlationId?: string | undefined;
  }[];
  readonly gmMessages: readonly {
    readonly id: number;
    readonly text: string;
    readonly severity: string;
  }[];
  readonly status: SeatSocketStatus;
  /**
   * Releases that have landed this window (`mw.released`).
   *
   * The wire payload rather than a reshaped copy, because the two things this
   * alert says are both fields on it and a schema change should reach here.
   * The server pushes `mw.released` to FIRES and OW alone
   * (`MW_CONSUMING_CELLS`), so **arrival is entitlement** — there is no cell
   * check below, and adding one would be the client holding a second opinion
   * about who may be told.
   */
  readonly mwReleases: readonly MwReleasedPayload[];
  readonly countdown: CountdownView;
  readonly turn: number | null;
  readonly dismissed: ReadonlySet<string>;
}

/** How many stack before the lane stops being a lane and starts being a wall. */
export const ALERT_LIMIT = 3;

const GRACE_SECONDS = Math.round(SEAT_GRACE_MS / 1000);

export function alertsFrom(sources: AlertSources): readonly SeatAlert[] {
  return alertsFromAll(sources).slice(0, ALERT_LIMIT);
}

/** Everything that qualifies, ranked, before the lane's cap is applied. */
function alertsFromAll(sources: AlertSources): readonly SeatAlert[] {
  const alerts: SeatAlert[] = [];

  // §3.2: the seat is held and the drafts survive. Saying so is the whole point
  // of this alert — a player who does not know it re-joins, and re-joining is
  // the one action that turns a recoverable drop into a lost seat.
  if (sources.status === 'reconnecting') {
    alerts.push({
      key: 'link:reconnecting',
      tone: 'error',
      title: 'RECONNECTING',
      body: `Your seat is held for ${GRACE_SECONDS} s; do not re-join.`,
      code: null,
      correlationId: null,
    });
  }

  for (const error of sources.errors) {
    alerts.push({
      key: `error:${error.id}`,
      tone: 'error',
      // The designed title, not the enum. This lane is the fourth place an
      // `ErrorCode` reached a player as a heading.
      title: errorTitle(error.code),
      body: error.message,
      code: error.code,
      correlationId: error.correlationId ?? null,
    });
  }

  /**
   * A launch was released, and Joint Fires has **no MW tab** by design (RB §2.4,
   * M4-D7): MW runs the recognition problem and FIRES shoots at what MW released.
   * Until this arm existed, a FIRES seat learned a launch was inbound only by
   * opening the target picker and finding a row that had not been there before —
   * an interrupt with no interrupt, which is the one thing the alert lane is for.
   *
   * Keyed on the event so a dismissal sticks to the launch it was aimed at, and
   * so a re-push or a resume replay cannot raise it twice. It goes with the
   * window: the socket clears `mwReleases` on `phase.changed`, and the intercept
   * this alert is about is only composable while that window is open.
   */
  for (const release of sources.mwReleases) {
    alerts.push({
      key: `mw:${release.eventId}`,
      tone: 'warning',
      title: 'LAUNCH RELEASED',
      // `assignedIdentity` is what the target picker's row says, so the alert and
      // the row name one thing. The stage modifier is deliberately not here —
      // it is on the row, where it is beside the order it modifies.
      body: release.interceptable
        ? `${release.assignedIdentity} — intercept available this window.`
        : `${release.assignedIdentity} — not interceptable.`,
      code: null,
      correlationId: null,
    });
  }

  for (const message of sources.gmMessages) {
    alerts.push({
      key: `gm:${message.id}`,
      tone: message.severity === 'CRITICAL' ? 'error' : 'warning',
      title: 'EXERCISE CONTROL',
      body: message.text,
      code: null,
      correlationId: null,
    });
  }

  // Keyed by turn so dismissing it this turn does not silence it next turn.
  if (sources.countdown.kind === 'URGENT' && sources.turn !== null) {
    alerts.push({
      key: `deadline:${sources.turn}`,
      tone: 'warning',
      title: 'WINDOW CLOSING',
      /*
       * Accepted orders resolve whether or not the seat pressed submit
       * (turn-machine.ts collects `accepted`, not `submitted`). At T-60 that is
       * the one fact worth more than the clock, and it is stated nowhere else.
       *
       * The clock used to lead this sentence and no longer does, for two
       * reasons. It duplicated the 23px readout a hundred pixels above it, which
       * the copy rules forbid; and `ErrorNote` carries `role="alert"`, so an
       * assertive region whose text changed every second re-announced the whole
       * warning sixty times in the final minute. The number lives in the status
       * row. This says the thing the status row cannot.
       */
      body: 'Queued orders resolve whether or not you submit.',
      code: null,
      correlationId: null,
    });
  }

  const live = alerts.filter((alert) => !sources.dismissed.has(alert.key));

  /*
   * Rank before truncating.
   *
   * The lane used to keep whatever `alertsFrom` happened to push first and drop
   * the rest silently, and `WINDOW CLOSING` was pushed **last** — so three GM
   * messages hid the highest-stakes alert of the turn, with nothing on screen to
   * say anything had been hidden. Push order is the order this function was
   * written in; it is not a priority.
   *
   * The order is what the operator loses by not seeing it: the link dropping
   * costs the seat, the window closing costs the turn, an error costs the
   * action, and a GM message is a message.
   */
  return [...live].sort((a, b) => rank(a) - rank(b));
}

/** Lower sorts first, and first is what survives `ALERT_LIMIT`. */
function rank(alert: SeatAlert): number {
  if (alert.key.startsWith('link:')) return 0;
  if (alert.key.startsWith('deadline:')) return 1;
  if (alert.tone === 'error') return 2;
  if (alert.key.startsWith('mw:')) return 3;
  return 4;
}

/**
 * How many alerts the lane could not show.
 *
 * Separate from `alertsFrom` so the truncation is a *fact the lane can render*
 * rather than something that happens silently inside a `slice`.
 */
export function alertOverflow(sources: AlertSources): number {
  const all = alertsFromAll(sources);
  return Math.max(0, all.length - ALERT_LIMIT);
}
